import {inject, injectable} from "inversify";
import {Observable, Observer} from "rxjs";
import {ListPaginationEntity} from "@dropDesk/domain/entities/common/list_pagination.entity";
import {InternalChatEntity} from "@dropDesk/domain/entities/internal_chat/internal_chat_entity";
import {InternalChatMessageEntity} from "@dropDesk/domain/entities/internal_chat/messages/internal_chat_message_entity";
import {InternalChatListFilters} from "@dropDesk/domain/entities/internal_chat/internal_chat_list_filters";
import {InternalChatUsersListFilters} from "@dropDesk/domain/entities/internal_chat/internal_chat_users_list_filters";
import {UserEntity} from "@dropDesk/domain/entities/user/user.entity";
import {socket} from "@dropDesk/data/clients/socket.client";
import {CancelTokenSource} from "axios";
import api, {parseServerError} from "@dropDesk/data/clients/http.client";
import {ConstantsKeys} from "@dropDesk/domain/entities/constants/constants_keys";
import {
    ListPaginationInternalChatEntity
} from "@dropDesk/domain/entities/internal_chat/list_pagination_internal_chat.entity";
import {InternalChatUserEntity} from "@dropDesk/domain/entities/internal_chat/users/internal_chat_user_entity";
import {BackendAction} from "@dropDesk/domain/entities/common/actions_entity";
import {DropDeskErrorEnum} from "@dropDesk/domain/entities/exceptions/dropdesk_error_enum";
import {RemoteStorageDatasource} from "@dropDesk/data/data_source/remote_storage/remote_storage_datasource";
import {SocketResponse, SocketResponseEntity} from "@dropDesk/domain/entities/chat/chat_socket";
import {ChatRemoteDataSource} from "@dropDesk/data/data_source/chat/chat_remote_datasource";

@injectable()
export abstract class InternalChatRemoteDataSource {
    public abstract listInternalChats(filters: InternalChatListFilters): Promise<ListPaginationEntity<InternalChatEntity>>;

    public abstract subscribe(): Observable<InternalChatEntity[]>;

    public abstract loadMessages(internalChatId: string, page: number, cancelTokenSource?: CancelTokenSource): Promise<ListPaginationEntity<InternalChatMessageEntity>>;

    public abstract sendMessage(message: InternalChatMessageEntity): Promise<InternalChatMessageEntity>;

    public abstract deleteMessage(id: string): Promise<void>;

    public abstract editMessage(idMessage: string, text: string): Promise<void>;

    public abstract informReadMessages(internalChatId: string, cancelTokenSource?: CancelTokenSource): Promise<void>;

    public abstract clearInternalChatsCache(): void;

    public abstract getInternalChatFromCache(id: string): InternalChatEntity | undefined;

    public abstract updateCacheFromSingleInternalChat(internalChat: InternalChatEntity): void;

    public abstract set(internalChat: InternalChatEntity): Promise<InternalChatEntity>;

    public abstract changeDescription(id: string, description: string): Promise<InternalChatEntity>;

    public abstract delete(id: string): Promise<void>;

    public abstract listUsers(filters: InternalChatUsersListFilters): Promise<ListPaginationEntity<InternalChatUserEntity>>;

    public abstract changeUsers(id: string, users: InternalChatUserEntity[]): Promise<InternalChatEntity>;

    public abstract subscriptionUploadFile(message: InternalChatMessageEntity): Observable<InternalChatMessageEntity>;

    public abstract navigateToMessage(id: string, idInternalChat: string): Promise<ListPaginationEntity<InternalChatMessageEntity>>;

    public abstract reactMessage(messageId: string, reactionId: string, reaction: string): Promise<void>;

    public abstract getUsersFromCache(ids: string[]): Promise<UserEntity[]>;

    public abstract getMessageById(id: string): Promise<InternalChatMessageEntity>;
}

@injectable()
export class InternalChatRemoteDataSourceImpl implements InternalChatRemoteDataSource {
    private _remoteDataSource: RemoteStorageDatasource;
    private _chatRemoteDataSource: ChatRemoteDataSource;
    private localCache: InternalChatEntity[] = [];
    private readonly table = 'internal-chat';
    private readonly tableMessages = 'internal-chat-messages';

    private get user(): UserEntity {
        return UserEntity.fromJson(JSON.parse(localStorage.getItem(process.env.STORAGE_USER as string)!));
    }

    constructor(
        @inject(RemoteStorageDatasource) dataSource: RemoteStorageDatasource,
        @inject(ChatRemoteDataSource) chatRemoteDataSource: ChatRemoteDataSource
    ) {
        this._remoteDataSource = dataSource;
        this._chatRemoteDataSource = chatRemoteDataSource;
    }

    processingReceived: string[] = [];

    public async listInternalChats(filters: InternalChatListFilters): Promise<ListPaginationEntity<InternalChatEntity>> {
        try {
            const response = await api.get(
                `${this.table}?searchParam=${filters.searchParam ? encodeURIComponent(filters.searchParam) : ''}&page=${filters.page}&limit=${filters.limit}&listOnlyDeleted=${filters.listOnlyDeleted}`,
                {cancelToken: filters.cancelTokenSource?.token}
            );
            return this.resolveListInternalChatResponse(response.data);
        } catch (error) {
            throw parseServerError(error);
        }
    }

    private resolveListInternalChatResponse(listPaginationEntity: ListPaginationInternalChatEntity): ListPaginationEntity<InternalChatEntity> {
        const internalChats = listPaginationEntity.data;
        let data: InternalChatEntity[] = [];

        if (internalChats.length > 0) {
            data = internalChats.map((entry: InternalChatEntity) => InternalChatEntity.fromJson(entry).copyWith({
                countUnreadNotification: listPaginationEntity.unreadMessages?.data?.find((chat) => chat?.idInternalChat === entry.id)?.total,
                users: entry.users?.map((user: InternalChatUserEntity) => InternalChatUserEntity.fromJson({
                    ...user,
                    action: user.action ?? BackendAction.nil
                })) ?? []
            }));
            
            const allUsers = data.flatMap(chat => chat.users?.map(u => u.user).filter(Boolean) ?? []);
            if (allUsers.length > 0) {
                this._chatRemoteDataSource.updateUsersInfoCache(allUsers as UserEntity[]);
            }
        }

        const result = new ListPaginationEntity<InternalChatEntity>({
            page: listPaginationEntity.page,
            limit: ConstantsKeys.defaultLimit,
            totalRows: listPaginationEntity.totalRows,
            hasMore: listPaginationEntity.page < (listPaginationEntity.pages - 1),
            pages: listPaginationEntity.pages,
            data,
        });

        this.updateLocalCache(result.data, false);
        return result;
    }

    private getLocalCacheDeepCopy(): InternalChatEntity[] {
        const copy = JSON.parse(JSON.stringify(this.localCache)) as any[];
        return copy.map((entry) => InternalChatEntity.fromJson(entry));
    }

    private getSoundPlayed(newInternalChat: InternalChatEntity, oldInternalChat: InternalChatEntity): boolean {
        return !!newInternalChat.lastMessage && oldInternalChat.lastMessage?.id !== newInternalChat.lastMessage.id;
    }

    private updateLocalCache(list: InternalChatEntity[], clearIfEmpty = true) {
        if (list.length === 0 && clearIfEmpty) {
            this.localCache = [];
        }
        if (list.length) {
            const deepLocalCacheCopy = this.getLocalCacheDeepCopy();
            for (const item of list) {
                const index = deepLocalCacheCopy.findIndex((internalChatCache) => internalChatCache.id === item.id);
                const checkPermission = item.hasEligibleInternalChat(this.user);
                if (index > -1) {
                    if (checkPermission) {
                        deepLocalCacheCopy[index] = item.copyWith({
                            countUnreadNotification: this.getCountUnreadNotification(item, deepLocalCacheCopy[index]),
                            soundPlayed: this.getSoundPlayed(item, deepLocalCacheCopy[index]),
                            messages: item?.messages?.length ? item.messages : []
                        });
                    } else {
                        deepLocalCacheCopy.splice(index, 1);
                    }

                } else {
                    deepLocalCacheCopy.push(
                        item.copyWith({
                            countUnreadNotification: this.getCountUnreadNotification(item),
                            users: item.users?.map((user: InternalChatUserEntity) => InternalChatUserEntity.fromJson({
                                ...user,
                                action: user.action ?? BackendAction.nil
                            })) ?? []
                        })
                    );
                }
            }

            this.localCache = deepLocalCacheCopy;
        }
    }

    private getCountUnreadNotification(newInternalChat: InternalChatEntity, internalChatCached?: InternalChatEntity) {
        if (
            newInternalChat.lastMessage &&
            !!internalChatCached &&
            !newInternalChat.lastMessage?.readers?.[this.user.id] &&
            (newInternalChat.lastMessage?.id !== internalChatCached?.lastMessage?.id)
        ) {
            return ((internalChatCached?.countUnreadNotification ?? 0) + 1)
        }
        if (internalChatCached) {
            return internalChatCached?.countUnreadNotification;
        }

        return newInternalChat.countUnreadNotification;
    }

    public subscribe(): Observable<InternalChatEntity[]> {
        return new Observable<InternalChatEntity[]>((observer: Observer<InternalChatEntity[]>) => {
            if (!socket) {
                console.error('Socket not connected');
                return;
            }
            socket.on('internalChatMessage', async (response: any) => {
                try {
                    const result = SocketResponse.fromJson(new SocketResponseEntity(response));
                    if (result.isInformRooms || result.isEmptyDataInternalCha()) {
                        observer.next(this.localCache);
                    } else if (result.hasUnknownError) {
                        observer.next(this.localCache);
                    } else {
                        this.normalizeSocketResponse(result);
                        const internalChats = await this.getTicketsAfterSubs(result.internalChats && result.internalChats.length > 0 ? result.internalChats : this.localCache);
                        observer.next(internalChats);
                    }
                } catch (error) {
                    console.error('Error processing internalChatMessage:', error);
                }
            });

            return () => {
                socket?.off('internalChatMessage');
            };
        });
    }

    public async loadMessages(internalChatId: string, page: number, cancelTokenSource?: CancelTokenSource): Promise<ListPaginationEntity<InternalChatMessageEntity>> {
        return new Promise<ListPaginationEntity<InternalChatMessageEntity>>(async (resolve, reject) => {
            try {
                const response = await api.get(`${this.tableMessages}/list?idInternalChat=${internalChatId}&page=${page}`, {cancelToken: cancelTokenSource?.token});
                const data = response.data.data.map((item: any) => InternalChatMessageEntity.fromJson(item));
                return resolve(new ListPaginationEntity<InternalChatMessageEntity>({
                    page: response.data.page,
                    limit: response.data.limit,
                    totalRows: response.data.totalRows,
                    hasMore: response.data.page < (response.data.pages - 1),
                    pages: response.data.pages,
                    data,
                }));
            } catch (error) {
                const errorParsed = parseServerError(error);
                if (errorParsed.message === DropDeskErrorEnum.operationCanceledDueToNewRequest) {
                    return;
                }
                return reject(errorParsed);
            }
        });
    }

    public sendMessage(message: InternalChatMessageEntity): Promise<InternalChatMessageEntity> {
        return new Promise<InternalChatMessageEntity>(async (resolve, reject) => {
            try {
                message = message.copyWith({
                    pushName: this.user.name
                }).prepareToSend();

                const result = await api.post(`${this.tableMessages}/writeMessage`, message.prepareToSend());


                message = message.copyWith({
                    id: result.data.id,
                    createdAt: result.data.createdAt,
                    status: 2
                });


                return resolve(message);
            } catch (error) {
                if (message.isFileMessage) {
                    const path = message.getPathToUpload();
                    await this._remoteDataSource.removeFile(path);
                }
                return reject(parseServerError(error));
            }
        });
    }

    public deleteMessage(id: string): Promise<void> {
        return new Promise<void>(async (resolve, reject) => {
            try {
                await api.delete(`${this.tableMessages}/deleteMessage?id=${id}`);
                return resolve();
            } catch (error) {
                return reject(parseServerError(error));
            }
        });
    }

    public editMessage(idMessage: string, text: string): Promise<void> {
        return new Promise<void>(async (resolve, reject) => {
            try {
                return resolve(await api.patch(`${this.tableMessages}/editMessage?id=${idMessage}`, {text}));
            } catch (error) {
                return reject(parseServerError(error));
            }
        });
    }

    public async informReadMessages(internalChatId: string, cancelTokenSource?: CancelTokenSource): Promise<void> {
        return new Promise<void>(async (resolve, reject) => {
            try {
                this.resetCountNotification(internalChatId);
                return resolve(await api.patch(`${this.tableMessages}/informReadMessages?idInternalChat=${internalChatId}`, {}, {cancelToken: cancelTokenSource?.token}));
            } catch (error) {
                const errorParsed = parseServerError(error);
                if (errorParsed.message === DropDeskErrorEnum.operationCanceledDueToNewRequest) {
                    return;
                }
                return reject(errorParsed);
            }
        });
    }

    public clearInternalChatsCache(): void {
        this.updateLocalCache([], true);
    }

    public getInternalChatFromCache(id: string): InternalChatEntity | undefined {
        return this.localCache.find((internalChat) => internalChat.id === id);
    }

    public updateCacheFromSingleInternalChat(internalChat: InternalChatEntity): void {
        this.updateLocalCache([internalChat], false);
    }

    public async set(internalChat: InternalChatEntity): Promise<InternalChatEntity> {
        try {
            const users: InternalChatUserEntity[] = internalChat.users?.filter(user => user.action !== BackendAction.nil) ?? [];
            const _internalChat = internalChat.copyWith({
                users: users.map(user => user.removeCalculatedVariables())
            });
            const response = await api.post(this.table, _internalChat.toJson());
            const result = InternalChatEntity.fromJson(response.data);
            
            const userEntities = result.users?.map(u => u.user).filter(Boolean) as UserEntity[];
            if (userEntities && userEntities.length > 0) {
                this._chatRemoteDataSource.updateUsersInfoCache(userEntities);
            }
            
            return result;
        } catch (error) {
            throw parseServerError(error);
        }
    }

    public async changeDescription(id: string, description: string): Promise<InternalChatEntity> {
        try {
            const response = await api.post(`${this.table}/${id}/changeDescription`, {description});
            return InternalChatEntity.fromJson(response.data);
        } catch (error) {
            throw parseServerError(error);
        }
    }

    public async delete(id: string): Promise<void> {
        try {
            await api.delete(`${this.table}/${id}`);
        } catch (error) {
            throw parseServerError(error);
        }
    }

    public async listUsers(filters: InternalChatUsersListFilters): Promise<ListPaginationEntity<InternalChatUserEntity>> {
        return new Promise<ListPaginationEntity<InternalChatUserEntity>>(async (resolve, reject) => {
            try {
                const response = await api.get(
                    `${this.table}/${filters.idInternalChat}/users?searchParam=${filters.searchParam ? encodeURIComponent(filters.searchParam) : ''}&page=${filters.page}&limit=${filters.limit}`,
                    {cancelToken: filters.cancelTokenSource?.token}
                );
                const users:InternalChatUserEntity[] = response.data.data.map((entry: InternalChatUserEntity) => InternalChatUserEntity.fromJson(entry));
                const userEntities = users.map(u => u.user).filter(Boolean) as UserEntity[];
                if (userEntities.length > 0) {
                    this._chatRemoteDataSource.updateUsersInfoCache(userEntities);
                }
                
                return resolve(new ListPaginationEntity<InternalChatUserEntity>({
                    page: filters.page,
                    limit: filters.limit,
                    totalRows: response.data.totalRows,
                    pages: response.data.pages,
                    hasMore: response.data.page < (response.data.pages - 1),
                    data: users,
                }));
            } catch (error) {
                const errorParsed = parseServerError(error);
                if (errorParsed.message === DropDeskErrorEnum.operationCanceledDueToNewRequest) {
                    return;
                }
                return reject(errorParsed);
            }
        });
    }

    public async changeUsers(id: string, users: InternalChatUserEntity[]): Promise<InternalChatEntity> {
        try {
            const usersToSend = users.filter(user => user.action !== BackendAction.nil).map(user => user.removeCalculatedVariables());
            const response = await api.post(`${this.table}/${id}/changeUsers`, {users: usersToSend});
            const result = InternalChatEntity.fromJson(response.data);
            
            const userEntities = result.users?.map(u => u.user).filter(Boolean) as UserEntity[];
            if (userEntities && userEntities.length > 0) {
                this._chatRemoteDataSource.updateUsersInfoCache(userEntities);
            }
            
            return result;
        } catch (error) {
            throw parseServerError(error);
        }
    }

    private resetCountNotification(internalChatId: string): void {
        let index: number | undefined = this.localCache.findIndex(item => internalChatId == item.id);
        if (index > -1) {
            this.localCache[index] = this.localCache[index].copyWith({
                countUnreadNotification: 0
            });
        }
    }

    public subscriptionUploadFile(message: InternalChatMessageEntity): Observable<InternalChatMessageEntity> {
        const basePath = `companies/${message.idCompany}/internalChats/${message.idInternalChat}`;
        return message.subscriptionUploadFile(this._remoteDataSource, basePath);
    }

    public navigateToMessage(id: string, idInternalChat: string): Promise<ListPaginationEntity<InternalChatMessageEntity>> {
        return new Promise<ListPaginationEntity<InternalChatMessageEntity>>(async (resolve, reject) => {
            try {
                const response = await api.get(`${this.tableMessages}/navigate-to-message?id=${id}&idInternalChat=${idInternalChat}`);
                const data: InternalChatMessageEntity[] = response.data.data.map((entry: InternalChatMessageEntity) => InternalChatMessageEntity.fromJson(entry));
                const result = new ListPaginationEntity<InternalChatMessageEntity>({
                    page: response.data.page,
                    limit: response.data.limit,
                    totalRows: response.data.totalRows,
                    hasMore: response.data.page < (response.data.pages - 1),
                    pages: response.data.pages,
                    data,
                });
                return resolve(result);
            } catch (error) {
                return reject(parseServerError(error));
            }
        });
    }

    public reactMessage(messageId: string, reactionId: string, reaction: string): Promise<void> {
        return new Promise<void>(async (resolve, reject) => {
            try {
                await api.post(`${this.tableMessages}/reactMessage?id=${messageId}&reactionId=${reactionId ?? ''}&reaction=${reaction ?? ''}`);
                return resolve();
            } catch (error) {
                return reject(parseServerError(error));
            }
        });
    }

    private normalizeSocketResponse(result: SocketResponse) {
        try {
            const messages = result.internalChatMessages ?? [];
            const groupedMessages = messages.reduce((acc, message) => {
                if (!acc[message.idInternalChat]) {
                    acc[message.idInternalChat] = [];
                }
                acc[message.idInternalChat].push(message);
                return acc;
            }, {} as Record<string, InternalChatMessageEntity[]>);

            for (const idInternalChat of result.getInternalChatList()) {
                if (!result.internalChats?.find(internalChat => internalChat.id === idInternalChat)) {
                    const internalChat = this.localCache.find((internalChat) =>
                        internalChat.id === idInternalChat
                    );
                    if (internalChat) {
                        result.internalChats = [...(result.internalChats ?? []), InternalChatEntity.fromJson(internalChat)];
                    }
                }

                const targetInternalChat = result.internalChats?.find(internalChat => internalChat.id === idInternalChat);

                if (targetInternalChat) {
                    const messages = groupedMessages[targetInternalChat.id] ?? [];
                    const currentInternalChatCache = this.localCache.find((internalChat) => internalChat.id === targetInternalChat.id);
                    const recentMessage = this.getMostRecentMessage(messages);
                    const isNewLastMessage = !!recentMessage &&
                        (
                            (!currentInternalChatCache?.lastMessage || !currentInternalChatCache.lastMessage.createdAt) ||
                            new Date(recentMessage.createdAt as any).getTime() >=
                            new Date(currentInternalChatCache.lastMessage.createdAt as any).getTime()
                        );
                    const realLastMessage = isNewLastMessage ? recentMessage : currentInternalChatCache?.lastMessage;
                    const index: number | undefined = result.internalChats!.findIndex(item => idInternalChat == item.id);
                    if (index > -1) {
                        if (currentInternalChatCache && currentInternalChatCache.updatedAt && (new Date(currentInternalChatCache.updatedAt).getTime() > new Date(targetInternalChat.updatedAt!).getTime())) {
                            result.internalChats![index] = InternalChatEntity.fromJson(currentInternalChatCache);
                        }
                        const updated = {
                            ...result.internalChats![index],
                            lastMessage: realLastMessage,
                            messages,
                        };
                        result.internalChats![index] = InternalChatEntity.fromJson(updated);
                    }

                }
            }
        } catch (error) {
            console.log(`Error ao processar resposta on method internalChatMessage: normalizeSocketResponse`);
            console.log(error);
        }
    }

    private getMostRecentMessage(messages: InternalChatMessageEntity[]): InternalChatMessageEntity | null {
        if (messages.length === 0) return null;

        return messages.reduce((latest, current) => {
            return new Date(current.createdAt!).getTime() > new Date(latest.createdAt!).getTime()
                ? current
                : latest;
        });
    }

    private makeDecodedInternalChats(): InternalChatEntity[] {
        return this.localCache.map((entry: InternalChatEntity) => InternalChatEntity.fromJson(new InternalChatEntity({
            ...entry,
            //TODO COLOCAR PRA PESQUISAR O USUARIO DO CACHE DO CHAT REMOTE DATASOURCE CASO PRECISAR
            // lastMessage: !!entry.lastMessage && !entry.lastMessage?.sender ?
            //     entry.lastMessage.copyWith({
            //         ...entry.lastMessage,
            //         sender: entry.usersInfo?.find((user) => user.id === entry.lastMessage!.senderId) ??
            //             UserEntity.fromJson({
            //                 id: entry.lastMessage!.senderId,
            //                 name: entry.lastMessage?.pushName ?? '',
            //             })
            //     }) : entry.lastMessage
        })));
    }

    private async getTicketsAfterSubs(internalChats: InternalChatEntity[]): Promise<InternalChatEntity[]> {
        this.processReceivedMessagesSocket(internalChats);
        return this.makeDecodedInternalChats();
    }

    private setProcessingReceived(internalChats: InternalChatEntity[]) {
        const ids = new Set(internalChats.map(internalChat => internalChat.id));
        this.processingReceived = [...this.processingReceived, ...ids];
    }

    private hasMessageNotReceived(internalChats: InternalChatEntity[], userId: string): boolean {
        return internalChats?.some(t => t.messages?.some(msg => !msg.received?.[userId])) ?? false;
    }

    private eligibleProcessingReceived(internalChats: InternalChatEntity[]): boolean {
        return !internalChats.every(t => this.processingReceived.includes(t.id)) && this.hasMessageNotReceived(internalChats, this.user.id);
    }

    private removeFromProcessingReceived(internalChats: InternalChatEntity[]): void {
        if (!internalChats?.length || !this.processingReceived?.length) return;

        const idsToRemove = new Set(internalChats.map(t => t.id));
        this.processingReceived = this.processingReceived.filter(id => !idsToRemove.has(id));
    }

    private processReceivedMessagesSocket(internalChats: InternalChatEntity[]): void {
        try {
            if (internalChats.length) {
                if (this.eligibleProcessingReceived(internalChats)) {
                    this.setProcessingReceived(internalChats);
                    api.patch(`${this.tableMessages}/processReceivedMessages?idInternalChat=${internalChats[0].id}`).finally(() => {
                        setTimeout(() => {
                            this.removeFromProcessingReceived(internalChats);
                        }, 5000);
                    });
                }
                this.updateLocalCache(internalChats);
            }
        } catch (error) {
            console.log('ERROR processReceivedMessageSocket', error);
        }
    }

    public getUsersFromCache(ids: string[]): Promise<UserEntity[]> {
        return this._chatRemoteDataSource.getUsersFromCache(ids);
    }

    public getMessageById(id: string): Promise<InternalChatMessageEntity> {
        return new Promise<InternalChatMessageEntity>(async (resolve, reject) => {
            try {
                const response = await api.get(`${this.tableMessages}/getMessageById?id=${id}`);
                const message = InternalChatMessageEntity.fromJson(response.data);
                return resolve(message);
            } catch (error) {
                return reject(parseServerError(error));
            }
        });
    }
}
